import {
  FormValues,
  RequestCallFormResponse,
} from '../../components/RequestCall/lazy/Form/FormTypes';
import {
  DoublePurchaseRequest,
  DoublePurchaseResponse,
  OrderPageDataRequest,
  DynamicOrderPageDataRequest,
  DynamicDoublePurchaseRequest,
  DynamicDoublePurchaseResponse,
} from '../../pages/CheckoutPage/CheckoutPageTypes';
import { URLS } from './Urls';
import {
  ChangePlanDetailsRequest,
  ChangePlanDetailsResult,
  DynamicChangePlanDetailsRequest,
  PlanFeaturesRequest,
} from '../../components/ChangePlan/ChangePlanTypes';
import { ProductTypeIds } from '../../constants';
import { SentryLogger } from '../SentryLogger';
import { RequestCallSource } from '../../enums';
import {
  PlanFeaturesResponse,
  ShouldSuggestReassignComposed,
  ShouldSuggestReassignRequest,
  ShouldSuggestReassignResponse,
} from '../../types';
import { HttpClient, HttpResponse } from '@wix/http-client';
import { wixResultInterceptor } from 'utils';
import { OrderPageDataResponse } from '@wix/checkout-components';

const httpClient = new HttpClient();
export default class Api {
  static PERMISSION_DENIED_ERROR_CODES = [
    'PERMISSION_DENIED',
    'PREMIUM_PERMISSION_DENIED',
  ];
  static BUSINESS_ERROR_CODES = [...this.PERMISSION_DENIED_ERROR_CODES];
  static fetchOrderPageData(
    params: OrderPageDataRequest,
  ): Promise<OrderPageDataResponse> {
    return httpClient
      .post(URLS.ORDER_PAGE_DATA, params)
      .then(({ data }) => data);
  }

  static fetchOrderPageDataV2(
    params: OrderPageDataRequest,
  ): Promise<OrderPageDataResponse> {
    return httpClient
      .post(URLS.ORDER_PAGE_DATA_V2, params)
      .then((response) => wixResultInterceptor(response))
      .then(({ data }) => {
        SentryLogger.addOrderSessionResponseToExtras(data);
        return data;
      });
  }
  static fetchDynmicOrderPageData(
    params: DynamicOrderPageDataRequest,
  ): Promise<OrderPageDataResponse> {
    return httpClient
      .post(URLS.DYNAMIC_ORDER_PAGE, params)
      .then((response) => wixResultInterceptor(response))
      .then(({ data }) => data);
  }

  static verifyDoublePurchase(
    params: DoublePurchaseRequest,
  ): Promise<DoublePurchaseResponse> {
    return httpClient
      .post(URLS.VERIFY_DOUBLE_PURCHASE, params)
      .then((response) => wixResultInterceptor(response))
      .then(({ data }) => data);
  }

  static verifyDynmaicDoublePurchase(
    params: DynamicDoublePurchaseRequest,
  ): Promise<DynamicDoublePurchaseResponse> {
    const { siteGuid } = params;
    const productTypeId = ProductTypeIds.PremiumPlans;

    return httpClient
      .post(URLS.DYNAMIC_VERIFY_DOUBLE_PURCHASE, {
        productTypeId,
        msid: siteGuid,
      })
      .then((response) => wixResultInterceptor(response))
      .then(({ data }) => data);
  }

  static fetchChangePlanDetails(
    params: ChangePlanDetailsRequest,
  ): Promise<ChangePlanDetailsResult> {
    return httpClient
      .get(`${URLS.CHANGE_PLAN_DETAILS}${Api.flatObjectToQuerystring(params)}`)
      .then((response) => wixResultInterceptor(response))
      .then(({ data, status }) => {
        if (status !== 200 || !data || !data.changePlanType) {
          throw data;
        }
        return data;
      });
  }

  static fetchDynamicChangePlanDetails(
    params: DynamicChangePlanDetailsRequest,
  ): Promise<ChangePlanDetailsResult> {
    return httpClient
      .get(
        `${URLS.DYNAMIC_CHANGE_PLAN_DETAILS}${Api.flatObjectToQuerystring(
          params,
        )}`,
      )
      .then((response) => wixResultInterceptor(response))
      .then(({ data, status }) => {
        if (status !== 200 || !data || !data.changePlanType) {
          throw data;
        }
        return data;
      });
  }

  static changePlanSwitchAndExtend(
    params: ChangePlanDetailsRequest,
  ): Promise<unknown> {
    return httpClient
      .post(URLS.SWITCH_AND_EXTEND, params)
      .then((response) => wixResultInterceptor(response))
      .then(({ data }) => data);
  }

  static changePlanDynamicSwitchAndExtend(
    params: DynamicChangePlanDetailsRequest,
  ): Promise<string> {
    return httpClient
      .post(URLS.DYNAMIC_SWITCH_AND_EXTEND, params)
      .then(({ data }) => data);
  }

  static changePlanSwitchAndRefund(
    params: ChangePlanDetailsRequest,
  ): Promise<unknown> {
    return httpClient
      .post(URLS.SWITCH_AND_REFUND, params)
      .then((response) => wixResultInterceptor(response))
      .then(({ data }) => data);
  }

  static changePlanDyanmicSwitchAndRefund(
    params: DynamicChangePlanDetailsRequest,
  ): Promise<string> {
    return httpClient
      .post(URLS.DYNAMIC_SWITCH_AND_REFUND, params)
      .then((response) => wixResultInterceptor(response))
      .then(({ data }) => data);
  }

  static changePlanSwitchInGracePeriod(
    params: ChangePlanDetailsRequest,
  ): Promise<unknown> {
    return httpClient
      .post(URLS.SWITCH_IN_GRACE_PERIOD, params)
      .then((response) => wixResultInterceptor(response))
      .then(({ data }) => data);
  }

  static changePlanDynamicSwitchInGracePeriod(
    params: DynamicChangePlanDetailsRequest,
  ): Promise<string> {
    return httpClient
      .post(URLS.DYNAMIC_SWITCH_IN_GRACE_PERIOD, params)
      .then((response) => wixResultInterceptor(response))
      .then(({ data }) => data);
  }

  static getPlanFeatures({
    planId,
    paymentCycle,
  }: PlanFeaturesRequest): Promise<PlanFeaturesResponse> {
    return httpClient
      .get(`${URLS.GET_PLAN_FEATURES}/${planId}?paymentCycle=${paymentCycle}`)
      .then(({ data }) => data);
  }

  static async getIsRepurchase(
    queryParams: ShouldSuggestReassignRequest,
  ): Promise<boolean> {
    return httpClient
      .post(URLS.SHOULD_SUGGEST_REASSIGN, queryParams)
      .then(({ data }: { data: ShouldSuggestReassignResponse }) => {
        (queryParams as ShouldSuggestReassignComposed).reassignStatus =
          data?.reassignStatus;
        return !!data?.reassignStatus;
      });
  }

  static RequestCallFormSubmit_V2(
    values: FormValues & {
      userGuid: string;
      locale: string;
      country: string;
      referral: string;
      source: RequestCallSource;
      clientId?: string;
    },
    requestCallNewAPI = false,
  ): Promise<HttpResponse<RequestCallFormResponse>> {
    const {
      firstName,
      lastName,
      company: companyName,
      jobTitle,
      messageBox: extraMessage,
      phoneNumber: phone,
      userGuid: userId,
      locale: language,
      country: countryCode,
      referral: inputReferral,
      bussinessEmail: email,
      source,
      clientId,
    } = values;
    const params = {
      email,
      firstName,
      lastName,
      phone,
      companyName,
      jobTitle,
      extraMessage,
      userId,
      language,
      countryCode,
      inputReferral,
      source,
      clientId,
    };

    return httpClient
      .post(
        requestCallNewAPI
          ? URLS.REQUEST_CALL_FORM_SUBMIT_V2
          : URLS.REQUEST_CALL_FORM_SUBMIT_CORVID,
        params,
      )
      .then((response) => wixResultInterceptor(response));
  }
  static getAssignedDomains(msid: string) {
    return httpClient.get(`${URLS.GET_ASSIGNED_DOMAINS}?filter.msid=${msid}`);
  }

  private static flatObjectToQuerystring(obj: any) {
    return Object.keys(obj).reduce(
      (str, key, i) => [str, i === 0 ? '?' : '&', key, '=', obj[key]].join(''),
      '',
    );
  }
}
